import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * ProtectedRoute - Rol tabanlı sayfa erişim kontrolü
 * 
 * Kullanım:
 * <ProtectedRoute allowedRoles={['kitchen_staff', 'manager']}>
 *   <KitchenScreen />
 * </ProtectedRoute>
 * 
 * Rol sistemi:
 * - owner / business_owner: tüm yetkiler
 * - manager: geniş yönetim yetkisi
 * - employee: genel erişim
 * - cashier: POS + ödeme
 * - kitchen_staff: mutfak ekranı + üretim terminali
 * - waiter: POS + masa yönetimi
 * - cari: B2B sipariş ekranı
 */

// Rol hiyerarşisi — Üst rol alt rollerin yetkilerini içerir
const ROLE_HIERARCHY = {
    owner: 10,
    business_owner: 10,
    super_admin: 10,
    system_admin: 10,
    admin: 9,
    manager: 8,
    employee: 4,
    cashier: 4,
    waiter: 4,
    kitchen_staff: 4,
    cari: 2
};

// localStorage'dan mevcut kullanıcı bilgilerini al
const getCurrentUser = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const staffUser = JSON.parse(sessionStorage.getItem('staff_user') || '{}');
        // Staff user (çalışan) öncelikli, yoksa normal user
        return staffUser?.role ? staffUser : user;
    } catch {
        return {};
    }
};

const ProtectedRoute = ({
    children,
    allowedRoles = [],          // Erişime izin verilen roller listesi
    requireAuth = true,         // Login zorunlu mu?
    redirectTo = '/login',      // Yetkisiz erişimde yönlendirilecek sayfa
    fallbackPath = '/unauthorized' // Rol yoksa gidilecek sayfa
}) => {
    const location = useLocation();
    const user = getCurrentUser();
    const token = localStorage.getItem('token') || sessionStorage.getItem('staff_token');

    // 1. Auth kontrolü — giriş yapılmış mı?
    if (requireAuth && !token) {
        return <Navigate to={redirectTo} state={{ redirectTo: location.pathname }} replace />;
    }

    // 2. Rol kontrolü — allowedRoles boşsa herkes girebilir (sadece login şartı)
    if (allowedRoles.length > 0 && user?.role) {
        const userRole = user.role;

        // owner veya admin ise her şeye erişebilir
        if (ROLE_HIERARCHY[userRole] >= 9) {
            return children;
        }

        // Rol listesinde var mı?
        if (!allowedRoles.includes(userRole)) {
            // Rol tabanlı varsayılan sayfaya yönlendir
            const defaultPath = getRoleDefaultPath(userRole);
            return <Navigate to={defaultPath} replace />;
        }
    }

    return children;
};

/**
 * Role göre varsayılan yönlendirme yolu
 */
export const getRoleDefaultPath = (role) => {
    // 🛡️ Admin Email Check (Highest priority fallback)
    const user = getCurrentUser();
    if (user?.email === 'admin@prmanager.com') {
        return '/dashboard/admin';
    }

    const roleRoutes = {
        kitchen_staff: '/sectors/food-beverage/kitchen/kitchen-screen',
        waiter: '/sectors/food-beverage/pos/restaurant-pos',
        cashier: '/sectors/food-beverage/pos/restaurant-pos',
        employee: '/dashboard/personal',
        manager: '/dashboard/personal',
        owner: '/dashboard/personal',
        business_owner: '/dashboard/personal',
        admin: '/dashboard/admin',
        system_admin: '/dashboard/admin',
        super_admin: '/dashboard/admin',
        cari: '/sectors/food-beverage/operations/distribution'
    };

    return roleRoutes[role] || (
        role === 'system_admin' || 
        role === 'admin' || 
        role === 'super_admin' 
            ? '/dashboard/admin' 
            : '/dashboard/personal'
    );
};

/**
 * Unauthorized sayfası
 */
export const UnauthorizedPage = () => {
    const user = getCurrentUser();
    const defaultPath = getRoleDefaultPath(user?.role);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🚫</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Erişim Reddedildi</h1>
                <p className="text-gray-500 mb-6">
                    Bu sayfaya erişim yetkiniz bulunmuyor.
                    {user?.role && (
                        <span className="block mt-1 text-sm font-medium text-gray-700">
                            Rolünüz: <span className="text-indigo-600">{user.role}</span>
                        </span>
                    )}
                </p>
                <a
                    href={defaultPath}
                    className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
                >
                    Ana Sayfama Dön
                </a>
            </div>
        </div>
    );
};

export default ProtectedRoute;
